import { useState, useRef, useMemo } from "react";
import { View, Text, Pressable, StyleSheet, Animated, PanResponder } from "react-native";
import { os } from "../platform";

// On Android a native-driven transform moves the view visually but leaves the
// touch hit-area at the original position, so Pressables inside swiped slides
// stop receiving taps. Disabling the native driver on Android keeps touch
// targets in sync with the animation. Web and iOS keep the native driver.
const USE_NATIVE_DRIVER = os !== "android";

const Carousel = ({ items = [], rightArrow }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const translateX = useRef(new Animated.Value(0)).current;
  const currentIndexRef = useRef(0);

  const onContainerLayout = (event) => {
    const { width } = event.nativeEvent.layout;
    setContainerWidth(width);
  };

  const goToNext = (width = containerWidth) => {
    if (items.length <= 1 || width === 0) return;

    const index = currentIndexRef.current;
    const isLooping = index === items.length - 1;
    const nextIndex = isLooping ? 0 : index + 1;

    if (isLooping) {
      Animated.timing(translateX, {
        toValue: -items.length * width,
        duration: 300,
        useNativeDriver: USE_NATIVE_DRIVER,
      }).start(() => {
        translateX.setValue(0);
        currentIndexRef.current = 0;
        setCurrentIndex(0);
      });
    } else {
      currentIndexRef.current = nextIndex;
      setCurrentIndex(nextIndex);
      Animated.timing(translateX, {
        toValue: -nextIndex * width,
        duration: 300,
        useNativeDriver: USE_NATIVE_DRIVER,
      }).start();
    }
  };

  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => items.length > 1,
        onMoveShouldSetPanResponder: (_, gestureState) =>
          items.length > 1 && Math.abs(gestureState.dx) > 5,
        onPanResponderRelease: (_, gestureState) => {
          const threshold = containerWidth * 0.2;
          if (gestureState.dx < -threshold || gestureState.vx < -0.3) {
            goToNext(containerWidth);
          }
        },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [items.length, containerWidth]
  );

  if (items.length === 0) return null;

  return (
    <View style={styles.container}>
      <View style={styles.slideContainer} onLayout={onContainerLayout} {...panResponder.panHandlers}>
        <Animated.View
          style={[
            styles.slidesWrapper,
            {
              transform: [{ translateX }],
              width: containerWidth * (items.length + 1),
            },
          ]}
        >
          {items.map((Component, index) => (
            <View key={index} style={[styles.slide, { width: containerWidth }]}>
              {Component}
            </View>
          ))}
          <View key="duplicate" style={[styles.slide, { width: containerWidth }]}>
            {items[0]}
          </View>
        </Animated.View>

        {items.length > 1 && (
          <Pressable
            testID="right-arrow"
            style={[styles.arrowButton, styles.rightArrow]}
            onPress={() => goToNext()}
            accessibilityRole="button"
            accessibilityLabel="Next item"
            accessibilityHint={`Item ${currentIndex + 1} of ${items.length}`}
          >
            {rightArrow || <Text style={styles.arrowText}>&gt;</Text>}
          </Pressable>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  slideContainer: {
    flex: 1,
    overflow: "hidden",
    position: "relative",
  },
  slidesWrapper: {
    flexDirection: "row",
    height: "100%",
  },
  slide: {
    justifyContent: "center",
    alignItems: "center",
  },
  arrowButton: {
    position: "absolute",
    transform: [{ translateY: 15 }],
    paddingHorizontal: 10,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1,
  },
  rightArrow: {
    right: -3,
  },
  arrowText: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#FFFFFF",
  },
});

export default Carousel;
